// components/Profile.js
import React, { useState, useEffect } from 'react';
import { getUserdatas } from '../../helper/generalHelper';
import api from '../../utils/api';
import { FaUserCircle } from 'react-icons/fa';

const StaffProfile = () => {
  const [userData, setUserData] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Load staff user data from local storage/helper
    const data = getUserdatas();
    if (data) {
      setUserData(data);
    }
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("profile_image", file);

    try {
      setUploading(true);
      const res = await api.post("restaurant-staff/update-profile-image", formData);
      if (res.success && res.data?.profile_image) {
        // update userData
        const updated = { ...userData, profile_image: res.data.profile_image };
        setUserData(updated);
        localStorage.setItem("userData", JSON.stringify(updated));
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Profile</h1>
      
      <div className="bg-white shadow-lg rounded-xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-[#f63b76] to-[#5846e5] h-32"></div>
        
        <div className="px-8 pb-8">
          {(!userData.roleName || !userData.restaurantName) && (
            <div className="mb-8 mt-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Notice: </strong>
              <span className="block sm:inline">Your profile is currently unassigned or incomplete. Please contact your administrator to assign a role and a business.</span>
            </div>
          )}
          {/* Profile Image Section */}
          <div className="flex flex-col mb-8 -mt-16">
            <div className="relative group w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-md bg-white">
              {userData.profile_image ? (
                <img 
                  src={`${process.env.REACT_APP_API_BASE_URL ? (process.env.REACT_APP_API_BASE_URL.endsWith('/') ? process.env.REACT_APP_API_BASE_URL : `${process.env.REACT_APP_API_BASE_URL}/`) : "http://localhost/stellaris_Original/stellaris_php-main/"}images/restaurant/staffs/${userData.profile_image}`} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-5xl">
                  <FaUserCircle />
                </div>
              )}
              {/* Upload Overlay */}
              <label className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 cursor-pointer transition-all duration-200">
                <span className="text-sm font-semibold">{uploading ? "Uploading..." : "Change Photo"}</span>
                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
              </label>
            </div>
            <div className="mt-4">
              <h2 className="text-2xl font-bold text-gray-800">{userData.firstName} {userData.lastName}</h2>
              <p className="text-gray-500 font-medium">{userData.roleName || 'Staff Member'}</p>
              {!userData.profile_image && (
                <p className="text-sm text-rose-500 mt-1 font-semibold animate-pulse">
                  ↑ Please upload your profile photo
                </p>
              )}
            </div>
          </div>

          {/* Details Table */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-white">
              <h3 className="text-lg font-semibold text-gray-800">Personal Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-100 transition-colors">
                    <th className="px-6 py-4 text-sm font-semibold text-gray-600 bg-gray-50 w-1/3">Full Name</th>
                    <td className="px-6 py-4 text-sm text-gray-800 bg-white">{userData.firstName} {userData.lastName}</td>
                  </tr>
                  <tr className="hover:bg-gray-100 transition-colors">
                    <th className="px-6 py-4 text-sm font-semibold text-gray-600 bg-gray-50">Email Address</th>
                    <td className="px-6 py-4 text-sm text-gray-800 bg-white">{userData.email || <span className="text-gray-400 italic">Not provided</span>}</td>
                  </tr>
                  <tr className="hover:bg-gray-100 transition-colors">
                    <th className="px-6 py-4 text-sm font-semibold text-gray-600 bg-gray-50">Mobile Number</th>
                    <td className="px-6 py-4 text-sm text-gray-800 bg-white">{userData.mobile || <span className="text-gray-400 italic">Not provided</span>}</td>
                  </tr>
                  <tr className="hover:bg-gray-100 transition-colors">
                    <th className="px-6 py-4 text-sm font-semibold text-gray-600 bg-gray-50">Assigned Role</th>
                    <td className="px-6 py-4 text-sm text-gray-800 bg-white">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {userData.roleName || 'Staff'}
                      </span>
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-100 transition-colors">
                    <th className="px-6 py-4 text-sm font-semibold text-gray-600 bg-gray-50">Assigned Business</th>
                    <td className="px-6 py-4 text-sm text-gray-800 bg-white">
                      {userData.restaurantName} <span className="text-gray-400 ml-1">(ID: {userData.restaurantCode})</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default StaffProfile;